import React, { useEffect, useMemo, useState } from "react";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useModalEscape from "../../hooks/useModalEscape";
import useModalTransition from "../../hooks/useModalTransition";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import Sidebar from "../../components/Sidebar/Sidebar";
import "./SA_UserManagement.css";

import {
  fetchRegions,
  fetchProvincesByRegion,
  fetchCitiesMunicipalitiesByProvince,
  fetchBarangaysByCityMunicipality,
} from "../../api/psgc";
import {
  provisionUser,
  validateLocation,
  validateStep1,
  validatePasswordsMatch,
} from "../../services/provisionUser";
import { notifyError, notifySuccess } from "../../notify";
import { listUsers } from "../../services/listUsers";
import { addUserCredits, updateUserProfile } from "../../services/updateUserProfile";
import { deleteUser } from "../../services/deleteUser";

// lucide-static icons
import {
  Activity,
  Users,
  Settings,
  Shield,
  Server,
  Search,
  Plus,
  UserPlus,
  X,
  Mail,
  User,
  Lock,
  Eye,
  EyeOff,
  MoreVertical,
  Edit,
  Trash2,
  AlertTriangle,
  Filter,
  Check,
  ChevronDown,
} from "lucide-static";
const avisaLogo = `${process.env.PUBLIC_URL}/images/avisa.svg`;

// Icon wrapper
const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    if (size) {
      svgString = svgString.replace(
        "<svg",
        `<svg width="${size}" height="${size}"`,
      );
    }
    if (color) {
      svgString = svgString.replace(
        "<svg",
        `<svg color="${color}" stroke="${color}"`,
      );
    }
    if (className) {
      svgString = svgString.replace("<svg", `<svg class="${className}"`);
    }
    return svgString;
  };
  return (
    <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />
  );
};

const SA_UserManagement = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const navigate = useNavigate();
  const location = useLocation();
  const { isSystemAdmin } = useAuth();

  const closeAllModals = () => {
    closeUserModal();
    closeAdminModal();
    closeEditModal();
    closeDeleteModal();
    closeAddCreditsModal();
    setIsPromoteModalOpen(false);
    setIsDemoteModalOpen(false);
    setIsFilterModalOpen(false);
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const USERS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = useState(false);
  const renderAddUserModal = useModalTransition(isAddUserModalOpen);
  const renderAddAdminModal = useModalTransition(isAddAdminModalOpen);
  const [addAdminStep, setAddAdminStep] = useState(1);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [isDemoteModalOpen, setIsDemoteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);
  const [isAddCreditsModalOpen, setIsAddCreditsModalOpen] = useState(false);
  const [addCreditsUser, setAddCreditsUser] = useState(null);
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    role: "all",
    displayMode: "name",
    sortBy: "newest",
  });
  const [tempFilters, setTempFilters] = useState(filters);

  useModalEscape(closeAllModals, isAddUserModalOpen || isAddAdminModalOpen || isEditUserModalOpen || isDeleteModalOpen || isAddCreditsModalOpen || isPromoteModalOpen || isDemoteModalOpen || isFilterModalOpen);

  const { users, usersLoaded, refreshUsers } = useData();
  const [usersLoading, setUsersLoading] = useState(!usersLoaded);

  const timeAgo = (ms) => {
    if (!ms || typeof ms !== "number") return "—";
    const diff = Date.now() - ms;
    if (diff < 60 * 1000) return "just now";
    const mins = Math.floor(diff / (60 * 1000));
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    if (days < 30) return `${days} days ago`;
    return new Date(ms).toLocaleDateString();
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    await refreshUsers();
    setUsersLoading(false);
  };

  useEffect(() => {
    if (usersLoaded) {
      setUsersLoading(false);
    }
  }, [usersLoaded]);

  // Add User (multi-step) modal state
  const [addUserStep, setAddUserStep] = useState(1);
  const [isAddUserSubmitting, setIsAddUserSubmitting] = useState(false);
  const [userModalError, setUserModalError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [basicUserInfo, setBasicUserInfo] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [psgc, setPsgc] = useState({
    regions: [],
    provinces: [],
    municipalities: [],
    barangays: [],
  });
  const [psgcLoading, setPsgcLoading] = useState({
    regions: false,
    provinces: false,
    municipalities: false,
    barangays: false,
  });
  const [psgcSelection, setPsgcSelection] = useState({
    regionCode: "",
    provinceCode: "",
    municipalityCode: "",
    barangayCode: "",
  });

  // Add Admin Modal form state
  const [adminFormData, setAdminFormData] = useState({
    email: "",
    fullName: "",
    username: "",
    password: "",
    confirmPassword: "",
    isSystemAdmin: false,
  });
  const [adminAccessLevel, setAdminAccessLevel] = useState("");
  const [adminModalError, setAdminModalError] = useState("");
  const [isAdminSubmitting, setIsAdminSubmitting] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showAdminConfirmPassword, setShowAdminConfirmPassword] = useState(false);

  // Edit User Modal form state
  const [editFormData, setEditFormData] = useState({
    email: "",
    fullName: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [editModalError, setEditModalError] = useState("");
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);

  const normalizeRole = (role) => {
    const value = String(role || "").trim().toLowerCase();
    if (value === "sysadmin" || value === "system_admin" || value === "system admin") return "sysadmin";
    if (value === "admin") return "admin";
    return "user";
  };

  const formatRoleLabel = (role) => {
    const normalized = normalizeRole(role);
    if (normalized === "sysadmin") return "System Administrator";
    if (normalized === "admin") return "Administrator";
    return "User";
  };

  const getUserAvatarSrc = (user) =>
    user?.profilePhoto || user?.profile?.profilePhoto || user?.photoURL || user?.photoUrl || null;

  // getActiveNav removed as it's now in the Sidebar component

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setBasicUserInfo((prev) => ({ ...prev, [name]: value }));
    if (userModalError) setUserModalError("");
  };

  const openAddUserModal = () => {
    setIsAddUserModalOpen(true);
    setAddUserStep(1);
    setIsAddUserSubmitting(false);
    setUserModalError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setBasicUserInfo({
      name: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setPsgc({ regions: [], provinces: [], municipalities: [], barangays: [] });
    setPsgcLoading({
      regions: false,
      provinces: false,
      municipalities: false,
      barangays: false,
    });
    setPsgcSelection({
      regionCode: "",
      provinceCode: "",
      municipalityCode: "",
      barangayCode: "",
    });
  };

  const openAddAdminModal = () => {
    setIsAddAdminModalOpen(true);
    setAddAdminStep(1);
    setIsAdminSubmitting(false);
    setAdminModalError("");
    setShowAdminPassword(false);
    setShowAdminConfirmPassword(false);
    setAdminFormData({
      email: "",
      fullName: "",
      username: "",
      password: "",
      confirmPassword: "",
      isSystemAdmin: false,
    });
    setAdminAccessLevel("");
    setPsgc({ regions: [], provinces: [], municipalities: [], barangays: [] });
    setPsgcLoading({
      regions: false,
      provinces: false,
      municipalities: false,
      barangays: false,
    });
    setPsgcSelection({
      regionCode: "",
      provinceCode: "",
      municipalityCode: "",
      barangayCode: "",
    });
  };

  // Handle Admin form input changes
  const handleAdminChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAdminFormData({
      ...adminFormData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (adminModalError) setAdminModalError("");
  };

  // Handle Edit form input changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
    if (editModalError) setEditModalError("");
  };

  const loadRegionsIfNeeded = async () => {
    if (psgc.regions.length > 0) return;
    setPsgcLoading((p) => ({ ...p, regions: true }));
    try {
      const regions = await fetchRegions();
      setPsgc((p) => ({ ...p, regions }));
    } catch (err) {
      notifyError("Failed to load regions.");
    } finally {
      setPsgcLoading((p) => ({ ...p, regions: false }));
    }
  };

  React.useEffect(() => {
    if (isAddUserModalOpen || isAddAdminModalOpen) {
      loadRegionsIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddUserModalOpen, isAddAdminModalOpen]);

  React.useEffect(() => {
    const regionCode = psgcSelection.regionCode;
    const isLocationStep = (isAddUserModalOpen && addUserStep === 2) || (isAddAdminModalOpen && addAdminStep === 2);
    if (!isLocationStep) return;
    if (!regionCode) return;

    setPsgcLoading((p) => ({ ...p, provinces: true }));
    fetchProvincesByRegion(regionCode)
      .then((provinces) => setPsgc((p) => ({ ...p, provinces })))
      .catch(() => notifyError("Failed to load provinces."))
      .finally(() => setPsgcLoading((p) => ({ ...p, provinces: false })));
  }, [psgcSelection.regionCode, isAddUserModalOpen, addUserStep, isAddAdminModalOpen, addAdminStep]);

  React.useEffect(() => {
    const provinceCode = psgcSelection.provinceCode;
    const isLocationStep = (isAddUserModalOpen && addUserStep === 2) || (isAddAdminModalOpen && addAdminStep === 2);
    if (!isLocationStep) return;
    if (!provinceCode) return;

    setPsgcLoading((p) => ({ ...p, municipalities: true }));
    fetchCitiesMunicipalitiesByProvince(provinceCode)
      .then((municipalities) => setPsgc((p) => ({ ...p, municipalities })))
      .catch(() => notifyError("Failed to load municipalities."))
      .finally(() =>
        setPsgcLoading((p) => ({ ...p, municipalities: false })),
      );
  }, [psgcSelection.provinceCode, isAddUserModalOpen, addUserStep, isAddAdminModalOpen, addAdminStep]);

  React.useEffect(() => {
    const municipalityCode = psgcSelection.municipalityCode;
    const isLocationStep = (isAddUserModalOpen && addUserStep === 2) || (isAddAdminModalOpen && addAdminStep === 2);
    if (!isLocationStep) return;
    if (!municipalityCode) return;

    setPsgcLoading((p) => ({ ...p, barangays: true }));
    fetchBarangaysByCityMunicipality(municipalityCode)
      .then((barangays) => setPsgc((p) => ({ ...p, barangays })))
      .catch(() => notifyError("Failed to load barangays."))
      .finally(() => setPsgcLoading((p) => ({ ...p, barangays: false })));
  }, [psgcSelection.municipalityCode, isAddUserModalOpen, addUserStep, isAddAdminModalOpen, addAdminStep]);

  const handleNextFromStep1 = async () => {
    const check = validateStep1(basicUserInfo);
    if (!check.ok) {
      notifyError(check.message);
      setUserModalError("");
      return;
    }

    const pw = validatePasswordsMatch(basicUserInfo);
    if (!pw.ok) {
      notifyError(pw.message);
      setUserModalError("");
      return;
    }

    setBasicUserInfo(check.normalized);
    setUserModalError("");
    setAddUserStep(2);

    // Ensure regions are loaded before Step 2 renders.
    await loadRegionsIfNeeded();
  };

  const handleFinalAddUser = async (e) => {
    e.preventDefault();
    if (isAddUserSubmitting) return;

    const regionName =
      psgc.regions.find((r) => r.code === psgcSelection.regionCode)?.name ||
      "";
    const provinceName =
      psgc.provinces.find((p) => p.code === psgcSelection.provinceCode)?.name ||
      "";
    const municipalityName =
      psgc.municipalities.find((m) => m.code === psgcSelection.municipalityCode)
        ?.name || "";
    const barangayName =
      psgc.barangays.find((b) => b.code === psgcSelection.barangayCode)?.name ||
      "";

    const locationCheck = validateLocation({
      region: regionName,
      province: provinceName,
      municipality: municipalityName,
      barangay: barangayName,
    });

    if (!locationCheck.ok) {
      notifyError(locationCheck.message);
      setUserModalError("");
      return;
    }

    setIsAddUserSubmitting(true);
    setUserModalError("");
    try {
      const result = await provisionUser({
        name: basicUserInfo.name,
        username: basicUserInfo.username,
        email: basicUserInfo.email,
        password: basicUserInfo.password,
        role: "user",
        adminaccessLevel: "none",
        location: locationCheck.normalized,
      });
      notifySuccess(result?.sentEmail ? 'User created. Password reset email sent.' : 'User created.');
      await loadUsers();
      closeUserModal();
    } catch (err) {
      notifyError(err?.message || "Failed to create user.");
    } finally {
      setIsAddUserSubmitting(false);
    }
  };

  const handleAdminNextFromStep1 = async () => {
    const check = validateStep1({
      name: adminFormData.fullName,
      username: adminFormData.username,
      email: adminFormData.email,
      password: adminFormData.password,
    });

    if (!check.ok) {
      notifyError(check.message);
      setAdminModalError("");
      return;
    }

    if (adminFormData.password !== adminFormData.confirmPassword) {
      notifyError("Passwords do not match!");
      setAdminModalError("");
      return;
    }

    if (!adminAccessLevel || adminAccessLevel === "") {
      notifyError("Please select Access (Provincial, Municipal or Barangay).");
      setAdminModalError("");
      return;
    }

    setAdminModalError("");
    setAddAdminStep(2);

    // Ensure regions are loaded before Step 2 renders.
    await loadRegionsIfNeeded();
  };

  // Handle Admin form submission
  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (isAdminSubmitting) return;

    const regionName =
      psgc.regions.find((r) => r.code === psgcSelection.regionCode)?.name ||
      "";
    const provinceName =
      psgc.provinces.find((p) => p.code === psgcSelection.provinceCode)?.name ||
      "";
    const municipalityName =
      psgc.municipalities.find((m) => m.code === psgcSelection.municipalityCode)
        ?.name || "";
    const barangayName =
      psgc.barangays.find((b) => b.code === psgcSelection.barangayCode)?.name ||
      "";

    const locationCheck = validateLocation({
      region: regionName,
      province: provinceName,
      municipality: municipalityName,
      barangay: barangayName,
    });

    if (!locationCheck.ok) {
      notifyError(locationCheck.message);
      setAdminModalError("");
      return;
    }

    setIsAdminSubmitting(true);
    setAdminModalError("");

    try {
      // require access selection for admin accounts
      if (!adminAccessLevel || adminAccessLevel === '') {
        notifyError('Please select Access (Provincial, Municipal or Barangay).');
        setIsAdminSubmitting(false);
        return;
      }
      const result = await provisionUser({
        name: adminFormData.fullName,
        username: adminFormData.username,
        email: adminFormData.email,
        password: adminFormData.password,
        role: "admin",
        adminaccessLevel: adminAccessLevel,
        location: locationCheck.normalized,
      });
      notifySuccess(result?.sentEmail ? 'Admin account created. Password reset email sent.' : 'Admin account created.');
      await loadUsers();
      closeAdminModal();
    } catch (err) {
      notifyError(err?.message || "Failed to create admin.");
      setAdminModalError("");
    } finally {
      setIsAdminSubmitting(false);
    }
  };

  // Handle Edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (isEditSubmitting) return;
    if (!selectedUser?.uid) {
      notifyError("Missing user id.");
      setEditModalError("");
      return;
    }

    if (editFormData.password !== editFormData.confirmPassword) {
      notifyError("Passwords do not match!");
      setEditModalError("");
      return;
    }

    if (editFormData.password && editFormData.password.length < 6) {
      notifyError("Password must be at least 6 characters");
      setEditModalError("");
      return;
    }

    setIsEditSubmitting(true);
    setEditModalError("");
    try {
      await updateUserProfile({
        uid: selectedUser.uid,
        name: editFormData.fullName,
        username: editFormData.username,
        password: editFormData.password,
      });
      notifySuccess("User updated.");
      await loadUsers();
      closeEditModal();
    } catch (err) {
      notifyError(err?.message || "Failed to update user.");
      setEditModalError("");
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleAddCredits = (targetUser = selectedUser) => {
    setAddCreditsUser(targetUser);
    setAddCreditsAmount('');
    setIsAddCreditsModalOpen(true);
  };

  const closeAddCreditsModal = () => {
    setIsAddCreditsModalOpen(false);
    setAddCreditsUser(null);
    setAddCreditsAmount('');
    setIsAddingCredits(false);
  };

  const confirmAddCredits = async () => {
    const amount = Number(addCreditsAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      notifyError('Enter a positive whole number of credits.');
      return;
    }

    setIsAddingCredits(true);
    try {
      const result = await addUserCredits({ uid: addCreditsUser?.uid, amount });
      setAddCreditsUser((previous) => ({
        ...previous,
        purchasedCredits: result.purchasedCredits,
        freeCredits: result.freeCredits,
      }));
      setAddCreditsAmount('');
      setIsAddingCredits(false);
      notifySuccess('Purchased credits added successfully.');
      closeAddCreditsModal();
    } catch (err) {
      notifyError(err?.message || 'Failed to add credits.');
      setIsAddingCredits(false);
    }
  };

  const handlePromoteToSystemAdmin = () => {
    setIsPromoteModalOpen(true);
  };

  const confirmPromoteUser = async () => {
    if (isPromoting) return;
    if (!selectedUser?.uid) {
      notifyError("Missing user id.");
      setEditModalError("");
      return;
    }

    setIsPromoting(true);
    setEditModalError("");
    try {
      await updateUserProfile({
        uid: selectedUser.uid,
        name: editFormData.fullName,
        username: editFormData.username,
        role: "sysadmin", // Admin SDK uses sysadmin internal mapping
      });
      notifySuccess("User promoted to System Admin.");
      await loadUsers();
      setIsPromoteModalOpen(false);
      closeEditModal();
    } catch (err) {
      notifyError(err?.message || "Failed to promote user.");
      setEditModalError("");
      setIsPromoteModalOpen(false);
    } finally {
      setIsPromoting(false);
    }
  };

  const handleDemoteToAdmin = () => {
    setIsDemoteModalOpen(true);
  };

  const confirmDemoteUser = async () => {
    if (isPromoting) return;
    if (!selectedUser?.uid) {
      notifyError("Missing user id.");
      setEditModalError("");
      return;
    }

    setIsPromoting(true);
    setEditModalError("");
    try {
      await updateUserProfile({
        uid: selectedUser.uid,
        name: editFormData.fullName,
        username: editFormData.username,
        role: "admin",
      });
      notifySuccess("Account role reassigned to Administrator.");
      await loadUsers();
      setIsDemoteModalOpen(false);
      closeEditModal();
    } catch (err) {
      notifyError(err?.message || "Failed to reassign role.");
      setEditModalError("");
      setIsDemoteModalOpen(false);
    } finally {
      setIsPromoting(false);
    }
  };

  const handleDeleteUser = (maybeUser) => {
    // If called as an onClick handler without wrapping, React will pass a click event.
    // We only accept actual user objects (must have uid).
    const user = (maybeUser && typeof maybeUser === "object" && "uid" in maybeUser)
      ? maybeUser
      : selectedUser;

    if (!user?.uid) {
      notifyError("Missing user id.");
      return;
    }

    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (isDeleteSubmitting) return;
    if (!userToDelete?.uid) {
      notifyError("Missing user id.");
      return;
    }

    setIsDeleteSubmitting(true);
    try {
      await deleteUser({ uid: userToDelete.uid });
      notifySuccess("User deleted.");
      await loadUsers();
      setIsDeleteModalOpen(false);
      setIsEditUserModalOpen(false);
      setUserToDelete(null);
      setSelectedUser(null);
    } catch (err) {
      notifyError(err?.message || "Failed to delete user.");
    } finally {
      setIsDeleteSubmitting(false);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
    setIsDeleteSubmitting(false);
  };

  // Open Edit Modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowEditPassword(false);
    setShowEditConfirmPassword(false);
    setEditFormData({
      email: user.email,
      fullName: user.name,
      username:
        user.username ||
        String(user.name || "")
          .toLowerCase()
          .trim()
          .replace(/\s/g, "_"),
      password: "",
      confirmPassword: "",
    });
    setIsEditUserModalOpen(true);
  };

  // Close User modal
  const closeUserModal = () => {
    setAddUserStep(1);
    setIsAddUserSubmitting(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setBasicUserInfo({
      name: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setPsgc({ regions: [], provinces: [], municipalities: [], barangays: [] });
    setPsgcLoading({
      regions: false,
      provinces: false,
      municipalities: false,
      barangays: false,
    });
    setPsgcSelection({
      regionCode: "",
      provinceCode: "",
      municipalityCode: "",
      barangayCode: "",
    });
    setUserModalError("");
    setIsAddUserModalOpen(false);
  };

  // Close Admin modal
  const closeAdminModal = () => {
    setAdminFormData({
      email: "",
      fullName: "",
      username: "",
      password: "",
      confirmPassword: "",
      isSystemAdmin: false,
    });
    setAdminModalError("");
    setIsAdminSubmitting(false);
    setShowAdminPassword(false);
    setShowAdminConfirmPassword(false);
    setAddAdminStep(1);
    setPsgc({ regions: [], provinces: [], municipalities: [], barangays: [] });
    setPsgcLoading({
      regions: false,
      provinces: false,
      municipalities: false,
      barangays: false,
    });
    setPsgcSelection({
      regionCode: "",
      provinceCode: "",
      municipalityCode: "",
      barangayCode: "",
    });
    setIsAddAdminModalOpen(false);
  };

  // Close Edit modal
  const closeEditModal = () => {
    setEditFormData({
      email: "",
      fullName: "",
      username: "",
      password: "",
      confirmPassword: "",
    });
    setEditModalError("");
    setIsEditSubmitting(false);
    setIsEditUserModalOpen(false);
    setSelectedUser(null);
    setIsPromoteModalOpen(false);
    setIsDemoteModalOpen(false);
  };

  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Search term filter - search name, username, and email
    const q = String(searchTerm || "").trim().toLowerCase();
    if (q) {
      result = result.filter((u) => {
        const name = String(u.name || "").toLowerCase();
        const username = String(u.username || "").toLowerCase();
        const email = String(u.email || "").toLowerCase();
        return (
          name.includes(q) || username.includes(q) || email.includes(q)
        );
      });
    }

    // Role filter
    if (filters.role !== "all") {
      result = result.filter((u) => {
        const userRole = String(u.role || "").toLowerCase();
        const targetRole = filters.role.toLowerCase();
        
        // Match "system_admin" or "system admin" etc.
        if (targetRole === "system admin" || targetRole === "system_admin") {
          return userRole === "system admin" || userRole === "system_admin";
        }
        return userRole === targetRole;
      });
    }

    // Sorting
    if (filters.sortBy === "newest") {
      result.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
    } else if (filters.sortBy === "oldest") {
      result.sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
    } else if (filters.sortBy === "alphabetical") {
      result.sort((a, b) => {
        const nameA = String(a.name || a.username || "").toLowerCase();
        const nameB = String(b.name || b.username || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    return result;
  }, [users, searchTerm, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * USERS_PER_PAGE;
    return filteredUsers.slice(start, start + USERS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, users, filters]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="dashboard-container">
      <Sidebar />

      <main className="main-content">
        {/* Top Bar - Search on LEFT, Buttons on RIGHT */}
        <div className="user-management-top-bar">
          {/* Search Section - LEFT side */}
          <div className="user-search-section">
              <div className="user-search-wrapper">
                <Icon icon={Search} size={18} className="user-search-icon" />
                <input
                  type="text"
                  placeholder="Search Users..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSearchTerm(searchInput);
                    }
                  }}
                  className="user-search-input"
                />
                <button
                  className={`user-filter-toggle-btn ${isFilterModalOpen ? "active" : ""}`}
                  onClick={() => {
                    if (!isFilterModalOpen) {
                      setTempFilters(filters);
                    }
                    setIsFilterModalOpen(!isFilterModalOpen);
                  }}
                  title="Filter & Sort"
                >
                  <Icon icon={Filter} size={18} />
                </button>

                {isFilterModalOpen && (
                  <>
                    <div
                      className="user-filter-modal-overlay"
                      onClick={() => setIsFilterModalOpen(false)}
                    />
                    <div className="user-filter-modal-content">
                      <div className="user-filter-modal-header">
                        <span className="user-filter-modal-title">Filter & Sort</span>
                        <button
                          className="user-filter-modal-close-icon"
                          onClick={() => setIsFilterModalOpen(false)}
                        >
                          <Icon icon={X} size={16} />
                        </button>
                      </div>

                      <div className="user-filter-modal-body">
                        <div className="user-filter-section">
                          <label className="user-filter-section-label">Role Filter</label>
                          <div className="user-filter-chips">
                            {["all", "system admin", "admin", "user"].map((r) => (
                              <button
                                key={r}
                                className={`user-filter-chip ${tempFilters.role === r ? "active" : ""}`}
                                onClick={() => setTempFilters({ ...tempFilters, role: r })}
                              >
                                {r.charAt(0).toUpperCase() + r.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="user-filter-section">
                          <label className="user-filter-section-label">Display Toggle</label>
                          <div className="user-filter-toggle-group">
                            <button
                              className={`user-filter-toggle-item ${tempFilters.displayMode === "name" ? "active" : ""}`}
                              onClick={() => setTempFilters({ ...tempFilters, displayMode: "name" })}
                            >
                              <span>Show Name</span>
                              {tempFilters.displayMode === "name" && (
                                <Icon icon={Check} size={14} className="check-icon" />
                              )}
                            </button>
                            <button
                              className={`user-filter-toggle-item ${tempFilters.displayMode === "username" ? "active" : ""}`}
                              onClick={() => setTempFilters({ ...tempFilters, displayMode: "username" })}
                            >
                              <span>Show Username</span>
                              {tempFilters.displayMode === "username" && (
                                <Icon icon={Check} size={14} className="check-icon" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="user-filter-section">
                          <label className="user-filter-section-label">Sort Options</label>
                          <div className="user-filter-sort-options">
                            {[
                              { id: "newest", label: "Newest → Oldest" },
                              { id: "oldest", label: "Oldest → Newest" },
                              { id: "alphabetical", label: "Alphabetical (A–Z)" },
                            ].map((s) => (
                              <button
                                key={s.id}
                                className={`user-filter-sort-item ${tempFilters.sortBy === s.id ? "active" : ""}`}
                                onClick={() => setTempFilters({ ...tempFilters, sortBy: s.id })}
                              >
                                {s.label}
                                {tempFilters.sortBy === s.id && (
                                  <Icon icon={Check} size={14} className="check-icon" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="user-filter-modal-footer">
                        <button
                          className="user-filter-reset-btn"
                          onClick={() =>
                            setTempFilters({
                              role: "all",
                              displayMode: "name",
                              sortBy: "newest",
                            })
                          }
                        >
                          Reset
                        </button>
                        <button
                          className="user-filter-apply-btn"
                          onClick={() => {
                            setFilters(tempFilters);
                            setIsFilterModalOpen(false);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
          </div>

          {/* Buttons Container - RIGHT side */}
          <div className="buttons-container">
            <button
              className="add-user-btn-filled"
              onClick={openAddUserModal}
            >
              Add User
            </button>
            <button
              className="add-admin-btn-outline"
              onClick={openAddAdminModal}
            >
              Add Admin
            </button>
          </div>
        </div>

        <div className="user-management-container">
          <div className="user-management-header">
            <h1 className="user-management-title">User Management</h1>
          </div>

          <p className="user-management-subtitle">
            Manage user accounts, permissions, and access
          </p>

          <div className="users-items-container">
            {usersLoading ? (
              <div className="no-users-found">
                <Icon icon={Users} size={48} className="no-users-icon" />
                <span>Loading users...</span>
              </div>
            ) : filteredUsers.length > 0 ? (
              paginatedUsers.map((user, index) => (
                <div key={user.uid || index} className="user-item">
                  <div className="user-item-left">
                    <div className="user-avatar-item">
                      {getUserAvatarSrc(user) ? (
                        <img
                          src={getUserAvatarSrc(user)}
                          alt=""
                          className="user-avatar-image"
                        />
                      ) : (
                        (user.name || user.username || "?").charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="user-info-item">
                      <span className="user-name-item">
                        {user.username && user.name
                          ? `${user.username} - ${user.name}`
                          : user.username || user.name || "—"}
                      </span>
                      <span className="user-email-item">{user.email || "—"}</span>
                    </div>
                  </div>
                  <div className="user-item-right">
                    <span className="user-role-badge">{formatRoleLabel(user.role)}</span>
                    <span className="user-time-item">
                      {timeAgo(user.lastActivityMs || user.lastLoginMs || user.lastSeenAtMs || user.createdAtMs)}
                    </span>
                    <button
                      type="button"
                      className="user-action-btn user-credits-btn"
                      onClick={() => handleAddCredits(user)}
                      title={`Add credits to ${user.name || user.username || user.email}`}
                      aria-label={`Add credits to ${user.name || user.username || user.email}`}
                      disabled={!isSystemAdmin() && normalizeRole(user.role) !== 'user'}
                    >
                      <Icon icon={Plus} size={18} />
                    </button>
                    <button
                      className="user-action-btn"
                      onClick={() => openEditModal(user)}
                      title={
                        isSystemAdmin() || normalizeRole(user.role) === 'user'
                          ? "Manage Account" 
                          : "Access Restricted"
                      }
                      disabled={
                        !isSystemAdmin() && normalizeRole(user.role) !== 'user'
                      }
                    >
                      <Icon icon={MoreVertical} size={18} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-users-found">
                <Icon icon={Users} size={48} className="no-users-icon" />
                <span>No users found</span>
              </div>
            )}
          </div>

          {!usersLoading && filteredUsers.length > 0 && (
            <div className="users-pagination">
              <span className="users-pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <div className="users-pagination-actions">
                <button
                  type="button"
                  className="users-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {(() => {
                  const maxVisiblePages = 20;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = startPage + maxVisiblePages - 1;

                  if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`users-pagination-btn ${currentPage === page ? "active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ));
                })()}
                <button
                  type="button"
                  className="users-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add User Modal */}
      {renderAddUserModal && (
        <div className={`user-modal-overlay modal-transition-${isAddUserModalOpen ? "enter" : "exit"}`} onClick={closeUserModal}>
          <div
            className="user-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="user-modal-close" onClick={closeUserModal}>
              &times;
            </button>

            {userModalError && (
              <div className="user-error-message">{userModalError}</div>
            )}
            {addUserStep === 1 ? (
              <div>
                <div className="user-modal-body">
                  <div className="user-form-field">
                    <label className="user-field-label">Name</label>
                    <div className="user-input-wrapper">
                      <Icon icon={User} size={18} className="user-input-icon" />
                      <input
                        type="text"
                        name="name"
                        value={basicUserInfo.name}
                        onChange={handleBasicInfoChange}
                        placeholder="Juan Dela Cruz"
                        className="user-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Username</label>
                    <div className="user-input-wrapper">
                      <Icon icon={User} size={18} className="user-input-icon" />
                      <input
                        type="text"
                        name="username"
                        value={basicUserInfo.username}
                        onChange={handleBasicInfoChange}
                        placeholder="juan_dela_cruz"
                        className="user-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Email</label>
                    <div className="user-input-wrapper">
                      <Icon icon={Mail} size={18} className="user-input-icon" />
                      <input
                        type="email"
                        name="email"
                        value={basicUserInfo.email}
                        onChange={handleBasicInfoChange}
                        placeholder="hello@example.com"
                        className="user-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Password</label>
                    <div className="user-input-wrapper">
                      <Icon icon={Lock} size={18} className="user-input-icon" />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword((p) => !p)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <Icon icon={showPassword ? EyeOff : Eye} size={18} />
                      </button>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={basicUserInfo.password}
                        onChange={handleBasicInfoChange}
                        placeholder="**********"
                        className="user-field-input-with-icon with-toggle"
                        required
                      />
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Confirm Password</label>
                    <div className="user-input-wrapper">
                      <Icon icon={Lock} size={18} className="user-input-icon" />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        aria-label={
                          showConfirmPassword
                            ? "Hide confirm password"
                            : "Show confirm password"
                        }
                      >
                        <Icon icon={showConfirmPassword ? EyeOff : Eye} size={18} />
                      </button>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={basicUserInfo.confirmPassword}
                        onChange={handleBasicInfoChange}
                        placeholder="**********"
                        className="user-field-input-with-icon with-toggle"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="user-modal-footer">
                  <button
                    type="button"
                    className="user-save-btn"
                    onClick={handleNextFromStep1}
                    disabled={isAddUserSubmitting}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleFinalAddUser}>
                <div className="user-modal-body">
                  <div className="user-form-field">
                    <label className="user-field-label">Region</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.regionCode}
                        onChange={(e) => {
                          const regionCode = e.target.value;
                          setPsgcSelection({
                            regionCode,
                            provinceCode: "",
                            municipalityCode: "",
                            barangayCode: "",
                          });
                          setPsgc((p) => ({
                            ...p,
                            provinces: [],
                            municipalities: [],
                            barangays: [],
                          }));
                          if (userModalError) setUserModalError("");
                        }}
                        className="user-field-select"
                        disabled={psgcLoading.regions}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.regions ? "Loading..." : "Select region"}
                        </option>
                        {psgc.regions.map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Province</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.provinceCode}
                        onChange={(e) => {
                          const provinceCode = e.target.value;
                          setPsgcSelection((prev) => ({
                            ...prev,
                            provinceCode,
                            municipalityCode: "",
                            barangayCode: "",
                          }));
                          setPsgc((p) => ({ ...p, municipalities: [], barangays: [] }));
                          if (userModalError) setUserModalError("");
                        }}
                        className="user-field-select"
                        disabled={!psgcSelection.regionCode || psgcLoading.provinces}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.provinces ? "Loading..." : "Select province"}
                        </option>
                        {psgc.provinces.map((p) => (
                          <option key={p.code} value={p.code}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Municipality</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.municipalityCode}
                        onChange={(e) => {
                          const municipalityCode = e.target.value;
                          setPsgcSelection((prev) => ({
                            ...prev,
                            municipalityCode,
                            barangayCode: "",
                          }));
                          setPsgc((p) => ({ ...p, barangays: [] }));
                          if (userModalError) setUserModalError("");
                        }}
                        className="user-field-select"
                        disabled={
                          !psgcSelection.provinceCode || psgcLoading.municipalities
                        }
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.municipalities
                            ? "Loading..."
                            : "Select municipality"}
                        </option>
                        {psgc.municipalities.map((m) => (
                          <option key={m.code} value={m.code}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Barangay</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.barangayCode}
                        onChange={(e) => {
                          setPsgcSelection((prev) => ({
                            ...prev,
                            barangayCode: e.target.value,
                          }));
                          if (userModalError) setUserModalError("");
                        }}
                        className="user-field-select"
                        disabled={!psgcSelection.municipalityCode || psgcLoading.barangays}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.barangays ? "Loading..." : "Select barangay"}
                        </option>
                        {psgc.barangays.map((b) => (
                          <option key={b.code} value={b.code}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="user-modal-footer">
                  <button
                    type="submit"
                    className="user-save-btn"
                    disabled={isAddUserSubmitting}
                  >
                    {isAddUserSubmitting ? "Adding..." : "Add User"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      {renderAddAdminModal && (
        <div className={`admin-modal-overlay modal-transition-${isAddAdminModalOpen ? "enter" : "exit"}`} onClick={closeAdminModal}>
          <div
            className="admin-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="admin-modal-close" onClick={closeAdminModal}>
              &times;
            </button>

            {adminModalError && (
              <div className="admin-error-message">{adminModalError}</div>
            )}

            {addAdminStep === 1 ? (
              <div>
                <div className="admin-modal-body">
                  <div className="admin-form-field">
                    <label className="admin-field-label">Email Address</label>
                    <div className="admin-input-wrapper">
                      <Icon icon={Mail} size={18} className="admin-input-icon" />
                      <input
                        type="email"
                        name="email"
                        value={adminFormData.email}
                        onChange={handleAdminChange}
                        placeholder="hello@example.com"
                        className="admin-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Full Name</label>
                    <div className="admin-input-wrapper">
                      <Icon icon={User} size={18} className="admin-input-icon" />
                      <input
                        type="text"
                        name="fullName"
                        value={adminFormData.fullName}
                        onChange={handleAdminChange}
                        placeholder="Juan Dela Cruz"
                        className="admin-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Username</label>
                    <div className="admin-input-wrapper">
                      <Icon icon={User} size={18} className="admin-input-icon" />
                      <input
                        type="text"
                        name="username"
                        value={adminFormData.username}
                        onChange={handleAdminChange}
                        placeholder="admin_user"
                        className="admin-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Password</label>
                    <div className="admin-input-wrapper">
                      <Icon icon={Lock} size={18} className="admin-input-icon" />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowAdminPassword((p) => !p)}
                        aria-label={showAdminPassword ? "Hide password" : "Show password"}
                      >
                        <Icon icon={showAdminPassword ? EyeOff : Eye} size={18} />
                      </button>
                      <input
                        type={showAdminPassword ? "text" : "password"}
                        name="password"
                        value={adminFormData.password}
                        onChange={handleAdminChange}
                        placeholder="**********"
                        className="admin-field-input-with-icon with-toggle"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Confirm Password</label>
                    <div className="admin-input-wrapper">
                      <Icon icon={Lock} size={18} className="admin-input-icon" />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowAdminConfirmPassword((p) => !p)}
                        aria-label={
                          showAdminConfirmPassword
                            ? "Hide confirm password"
                            : "Show confirm password"
                        }
                      >
                        <Icon icon={showAdminConfirmPassword ? EyeOff : Eye} size={18} />
                      </button>
                      <input
                        type={showAdminConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={adminFormData.confirmPassword}
                        onChange={handleAdminChange}
                        placeholder="**********"
                        className="admin-field-input-with-icon with-toggle"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Access</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={adminAccessLevel}
                        onChange={(e) => setAdminAccessLevel(e.target.value)}
                        className="admin-field-select"
                      >
                        <option value="">Select Access Level</option>
                        <option value="provincial">Provincial</option>
                        <option value="municipal">Municipal</option>
                        <option value="barangay">Barangay</option>
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="admin-modal-footer">
                  <button
                    type="button"
                    className="admin-save-btn"
                    onClick={handleAdminNextFromStep1}
                    disabled={isAdminSubmitting || !adminAccessLevel}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAdminSubmit}>
                <div className="admin-modal-body">
                  {/* Step 2: Location Information (Consistent with Add User) */}
                  <div className="admin-form-field">
                    <label className="admin-field-label">Region</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.regionCode}
                        onChange={(e) => {
                          const regionCode = e.target.value;
                          setPsgcSelection({
                            regionCode,
                            provinceCode: "",
                            municipalityCode: "",
                            barangayCode: "",
                          });
                          setPsgc((p) => ({
                            ...p,
                            provinces: [],
                            municipalities: [],
                            barangays: [],
                          }));
                          if (adminModalError) setAdminModalError("");
                        }}
                        className="user-field-select"
                        disabled={psgcLoading.regions}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.regions ? "Loading..." : "Select region"}
                        </option>
                        {psgc.regions.map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Province</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.provinceCode}
                        onChange={(e) => {
                          const provinceCode = e.target.value;
                          setPsgcSelection((prev) => ({
                            ...prev,
                            provinceCode,
                            municipalityCode: "",
                            barangayCode: "",
                          }));
                          setPsgc((p) => ({ ...p, municipalities: [], barangays: [] }));
                          if (adminModalError) setAdminModalError("");
                        }}
                        className="user-field-select"
                        disabled={!psgcSelection.regionCode || psgcLoading.provinces}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.provinces ? "Loading..." : "Select province"}
                        </option>
                        {psgc.provinces.map((p) => (
                          <option key={p.code} value={p.code}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Municipality</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.municipalityCode}
                        onChange={(e) => {
                          const municipalityCode = e.target.value;
                          setPsgcSelection((prev) => ({
                            ...prev,
                            municipalityCode,
                            barangayCode: "",
                          }));
                          setPsgc((p) => ({ ...p, barangays: [] }));
                          if (adminModalError) setAdminModalError("");
                        }}
                        className="user-field-select"
                        disabled={
                          !psgcSelection.provinceCode || psgcLoading.municipalities
                        }
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.municipalities
                            ? "Loading..."
                            : "Select municipality"}
                        </option>
                        {psgc.municipalities.map((m) => (
                          <option key={m.code} value={m.code}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="admin-form-field">
                    <label className="admin-field-label">Barangay</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.barangayCode}
                        onChange={(e) => {
                          setPsgcSelection((prev) => ({
                            ...prev,
                            barangayCode: e.target.value,
                          }));
                          if (adminModalError) setAdminModalError("");
                        }}
                        className="user-field-select"
                        disabled={!psgcSelection.municipalityCode || psgcLoading.barangays}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.barangays ? "Loading..." : "Select barangay"}
                        </option>
                        {psgc.barangays.map((b) => (
                          <option key={b.code} value={b.code}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="admin-modal-footer">
                  <button 
                    type="submit" 
                    className="admin-save-btn"
                    disabled={isAdminSubmitting}
                  >
                    {isAdminSubmitting ? "Adding..." : "Add Admin"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditUserModalOpen && selectedUser && (
        <div className="edit-modal-overlay" onClick={closeEditModal}>
          <div
            className="edit-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="edit-modal-close" onClick={closeEditModal}>
              &times;
            </button>

            <h2 className="edit-modal-title">Manage User Account</h2>

            {editModalError && (
              <div className="edit-error-message">{editModalError}</div>
            )}

            <form onSubmit={handleEditSubmit}>
              <div className="edit-modal-body">
                <div className="edit-form-field">
                  <label className="edit-field-label">Email Address</label>
                  <div className="edit-input-wrapper">
                    <Icon icon={Mail} size={18} className="edit-input-icon" />
                    <input
                      type="email"
                      name="email"
                      value={editFormData.email}
                      readOnly
                      placeholder="hello@example.com"
                      className="edit-field-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div className="edit-form-field">
                  <label className="edit-field-label">Full Name</label>
                  <div className="edit-input-wrapper">
                    <Icon icon={User} size={18} className="edit-input-icon" />
                    <input
                      type="text"
                      name="fullName"
                      value={editFormData.fullName}
                      onChange={handleEditChange}
                      placeholder="Juan Dela Cruz"
                      className="edit-field-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div className="edit-form-field">
                  <label className="edit-field-label">Username</label>
                  <div className="edit-input-wrapper">
                    <Icon icon={User} size={18} className="edit-input-icon" />
                    <input
                      type="text"
                      name="username"
                      value={editFormData.username}
                      onChange={handleEditChange}
                      placeholder="username"
                      className="edit-field-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div className="edit-form-field">
                  <label className="edit-field-label">New Password</label>
                  <div className="edit-input-wrapper">
                    <Icon icon={Lock} size={18} className="edit-input-icon" />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowEditPassword((p) => !p)}
                      aria-label={showEditPassword ? "Hide password" : "Show password"}
                    >
                      <Icon icon={showEditPassword ? EyeOff : Eye} size={18} />
                    </button>
                    <input
                      type={showEditPassword ? "text" : "password"}
                      name="password"
                      value={editFormData.password}
                      onChange={handleEditChange}
                      placeholder="**********"
                      className="edit-field-input-with-icon with-toggle"
                    />
                  </div>
                </div>

                <div className="edit-form-field">
                  <label className="edit-field-label">
                    Confirm New Password
                  </label>
                  <div className="edit-input-wrapper">
                    <Icon icon={Lock} size={18} className="edit-input-icon" />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowEditConfirmPassword((p) => !p)}
                      aria-label={
                        showEditConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                    >
                      <Icon icon={showEditConfirmPassword ? EyeOff : Eye} size={18} />
                    </button>
                    <input
                      type={showEditConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={editFormData.confirmPassword}
                      onChange={handleEditChange}
                      placeholder="**********"
                      className="edit-field-input-with-icon with-toggle"
                    />
                  </div>
                </div>

                {normalizeRole(selectedUser.role) === "admin" && (
                  <div className="edit-form-field promote-field-container">
                    <button
                      type="button"
                      className="promote-admin-btn"
                      onClick={handlePromoteToSystemAdmin}
                      disabled={isPromoting}
                    >
                      Assign System Administrator Role
                    </button>
                  </div>
                )}

                {normalizeRole(selectedUser.role) === "sysadmin" && (
                  <div className="edit-form-field promote-field-container">
                    <button
                      type="button"
                      className="promote-admin-btn"
                      onClick={handleDemoteToAdmin}
                      disabled={isPromoting}
                    >
                      Reassign to Administrator Role
                    </button>
                  </div>
                )}
              </div>

              <div className="edit-modal-footer">
                <button
                  type="button"
                  className="edit-delete-btn"
                  onClick={() => handleDeleteUser(selectedUser)}
                >
                  Remove Account
                </button>
                <button type="submit" className="edit-save-btn">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isPromoteModalOpen && (
        <div className="promote-modal-overlay" onClick={() => setIsPromoteModalOpen(false)}>
          <div
            className="promote-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="promote-modal-header">
              <div className="promote-warning-icon">
                <Icon icon={Shield} size={32} />
              </div>
              <h2 className="promote-modal-title">Assign System Administrator Role?</h2>
              <p className="promote-modal-subtitle">
                Confirm role assignment for{" "}
                <strong>{selectedUser?.name}</strong> to{" "}
                <strong>System Administrator</strong>. This grants full system access.
              </p>
            </div>
            <div className="promote-modal-footer">
              <button className="promote-cancel-btn" onClick={() => setIsPromoteModalOpen(false)}>
                Cancel
              </button>
              <button
                className="promote-confirm-btn"
                onClick={confirmPromoteUser}
                disabled={isPromoting}
              >
                {isPromoting ? "Assigning..." : "Confirm Assignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDemoteModalOpen && (
        <div className="promote-modal-overlay" onClick={() => setIsDemoteModalOpen(false)}>
          <div
            className="promote-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="promote-modal-header">
              <div className="promote-warning-icon">
                <Icon icon={Shield} size={32} />
              </div>
              <h2 className="promote-modal-title">Reassign to Administrator Role?</h2>
              <p className="promote-modal-subtitle">
                Confirm role reassignment for <strong>{selectedUser?.name}</strong> from <strong>System Administrator</strong> to <strong>Administrator</strong>.
              </p>
            </div>
            <div className="promote-modal-footer">
              <button className="promote-cancel-btn" onClick={() => setIsDemoteModalOpen(false)}>
                Cancel
              </button>
              <button
                className="promote-confirm-btn"
                onClick={confirmDemoteUser}
                disabled={isPromoting}
              >
                {isPromoting ? "Updating..." : "Confirm Reassignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="delete-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-modal-header">
              <div className="delete-warning-icon">
                <Icon icon={AlertTriangle} size={32} />
              </div>
              <h2 className="delete-modal-title">Remove Account?</h2>
              <p className="delete-modal-subtitle">
                Are you sure you want to remove{" "}
                <strong>{userToDelete?.name}</strong>? This action cannot be
                undone.
              </p>
            </div>
            <div className="delete-modal-footer">
              <button className="delete-cancel-btn" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button
                className="delete-confirm-btn"
                onClick={confirmDeleteUser}
                disabled={isDeleteSubmitting}
              >
                {isDeleteSubmitting ? "Removing..." : "Confirm Removal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddCreditsModalOpen && (
        <div className="delete-modal-overlay" onClick={closeAddCreditsModal}>
          <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h2 className="delete-modal-title">Add Purchased Credits</h2>
              <p className="delete-modal-subtitle">
                Add credits to <strong>{addCreditsUser?.name || addCreditsUser?.email || 'this user'}</strong>.
              </p>
              <p className="delete-modal-subtitle">
                Current total balance: <strong>{Number(addCreditsUser?.purchasedCredits || 0) + Number(addCreditsUser?.freeCredits || 0)}</strong>
              </p>
              <div className="user-input-wrapper">
                <Icon icon={Plus} size={18} className="user-input-icon" />
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={addCreditsAmount}
                  onChange={(e) => setAddCreditsAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  placeholder="Credits to add"
                  className="user-field-input-with-icon"
                  autoFocus
                />
              </div>
            </div>
            <div className="delete-modal-footer">
              <button type="button" className="delete-cancel-btn" onClick={closeAddCreditsModal} disabled={isAddingCredits}>
                Cancel
              </button>
              <button
                type="button"
                className="delete-confirm-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  confirmAddCredits();
                }}
                disabled={isAddingCredits}
              >
                {isAddingCredits ? 'Adding...' : 'Add Credits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SA_UserManagement;
